// ════════════════════════════════════════════════════════════════════
// MATCH REVIEW VIEW — Lead ↔ Book-of-Business cross-reference (PDF Prompt 1).
//
// Three sections:
//   1. "Scan now" — sends the operator's in-app transactions array to
//      /api/cross-ref/upsert-book. The server rolls them up into
//      book_clients, runs the three-tier matcher against leads (read
//      from user_state JSON), and writes client_lead_links.
//   2. Review queue — rows where status='review'. Each row shows the
//      book side (insured, policy, carrier, writing agent, signals) and
//      the candidate lead(s). Two actions: Confirm / Reject.
//   3. Confirmed/Auto-matched history — read-only cross-references.
//
// Backed entirely by /api/cross-ref/links and /api/cross-ref/upsert-book.
// ════════════════════════════════════════════════════════════════════

import React from 'react';
import { Sparkles, CheckCircle, X, AlertCircle } from 'lucide-react';
import { readPersistedJwtDirect } from '../lib/authedFetch';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
}

export default function MatchReviewView({ currentUser, showToast = () => {} }) {
  const [links, setLinks] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('review');
  const [loading, setLoading] = React.useState(true);
  const [scanning, setScanning] = React.useState(false);
  const [error, setError]   = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const json = await apiFetch(`/api/cross-ref/links?status=${encodeURIComponent(statusFilter)}`);
      setLinks(Array.isArray(json?.links) ? json.links : []);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  React.useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      // Pull the operator's transactions from app state — same array
      // CommissionTracker.jsx renders everywhere. We grab it from
      // localStorage as a fallback when this component is mounted in
      // isolation; in normal use the parent injects via props later.
      let transactions = [];
      try {
        const raw = localStorage.getItem(`crm:state:${currentUser?.id || ''}`);
        const st  = raw ? JSON.parse(raw) : null;
        transactions = Array.isArray(st?.transactions) ? st.transactions : [];
      } catch { /* ignore */ }
      if (transactions.length === 0) {
        showToast('No transactions in app state — upload a statement first.');
        setScanning(false);
        return;
      }
      const json = await apiFetch('/api/cross-ref/upsert-book', {
        method: 'POST',
        body:   JSON.stringify({ transactions }),
      });
      showToast(`Scanned ${transactions.length} transactions → ${json.upserted_book_clients} book clients. Auto: ${json.upserted_links_by_tier?.policy_exact || 0} policy, ${json.upserted_links_by_tier?.name_state || 0} name+state. Review: ${json.upserted_links_by_tier?.name_fuzzy || 0}.`);
      load();
    } catch (err) {
      showToast(`Scan failed: ${err.message || err}`);
    } finally {
      setScanning(false);
    }
  };

  const decide = async (id, action) => {
    try {
      await apiFetch('/api/cross-ref/links', {
        method: 'POST',
        body:   JSON.stringify({ id, action }),
      });
      showToast(action === 'confirm' ? 'Link confirmed.' : 'Link rejected.');
      load();
    } catch (err) {
      showToast(`Action failed: ${err.message || err}`);
    }
  };

  // Group review links by book_client so each row shows the candidate list.
  const grouped = React.useMemo(() => {
    const m = new Map();
    for (const l of links) {
      const k = l.book_client_id;
      if (!m.has(k)) m.set(k, { book: l.book, candidates: [] });
      m.get(k).candidates.push(l);
    }
    for (const g of m.values()) g.candidates.sort((a, b) => (a.candidate_rank || 0) - (b.candidate_rank || 0));
    return [...m.values()];
  }, [links]);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-xl p-5">
        <div className="text-amber-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Cross-Reference</div>
        <h1 className="text-xl font-bold mt-1">Match Review · Lead ↔ Book of Business</h1>
        <p className="text-slate-300 text-sm mt-2">
          The matcher links carrier-statement clients to leads using three tiers: policy-exact (auto),
          MOO full-name + state (auto), and soundex fuzzy (review). Confirmed links show as cross-references
          on both records — the two data sets stay stored separately.
        </p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={runScan}
            disabled={scanning}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-bold text-sm px-4 py-2 rounded-md"
          >
            {scanning ? 'Scanning…' : 'Scan transactions now'}
          </button>
          {['review','auto_matched','confirmed','rejected','all'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-bold px-3 py-1.5 rounded-md border ${statusFilter === s ? 'bg-white text-slate-900 border-white' : 'bg-transparent text-slate-300 border-slate-600 hover:border-slate-400'}`}
            >{s}</button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 p-6">Loading match review…</div>
      ) : grouped.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500 italic">
          No {statusFilter === 'all' ? '' : statusFilter + ' '}links yet. Click <strong>Scan transactions now</strong> to run the matcher.
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(({ book, candidates }) => (
            <div key={book?.id || candidates[0].id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Book client</div>
                    <div className="font-bold text-slate-900">{book?.insured_raw || candidates[0].signals?.last || '(unknown)'}</div>
                    <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="font-mono">{book?.company}</span> ·
                      <span className="font-mono">{book?.policy}</span> ·
                      {book?.state && <span>{book.state}</span>}
                      {book?.prod_num && <span>writing agent {book.prod_num}</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {candidates.map(c => {
                  const tType = c.target_type || 'lead';
                  const isOrphan = tType === 'orphan';
                  return (
                  <div key={c.id} className="px-5 py-3 flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">{c.match_method}</span>
                        {!isOrphan && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200">confidence {c.confidence}</span>}
                        {/* target_type badge — lead vs client vs orphan. Tells the operator
                            whether the candidate is from their prospect pipeline or already
                            on their book of business. */}
                        {tType === 'lead' && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200">lead</span>}
                        {tType === 'client' && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">client</span>}
                        {isOrphan && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">no match — new?</span>}
                        {c.candidate_rank && <span className="text-[10px] text-slate-500">rank #{c.candidate_rank}</span>}
                      </div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {isOrphan
                          ? <span className="italic text-amber-700">Not found in your leads or clients — create a profile?</span>
                          : (c.lead ? c.lead.name : <span className="italic text-slate-400">{tType} not found (id: {String(c.lead_id || '').slice(0, 8)})</span>)
                        }
                      </div>
                      {c.lead && !isOrphan && (
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {c.lead.phone && <span>{c.lead.phone}</span>}
                          {c.lead.email && <span>{c.lead.email}</span>}
                          {c.lead.state && <span>{c.lead.state}</span>}
                          {c.lead.status && <span>status: {c.lead.status}</span>}
                        </div>
                      )}
                    </div>
                    {c.status === 'review' && isOrphan && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => decide(c.id, 'create_client')}
                          className="bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md inline-flex items-center gap-1"
                          title="Create a new client profile from the book client info"
                        >
                          <CheckCircle size={12}/> Create client
                        </button>
                        <button
                          onClick={() => decide(c.id, 'reject')}
                          className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md inline-flex items-center gap-1"
                          title="Not a new client (duplicate, spouse already covered, junk, etc.)"
                        >
                          <X size={12}/> Dismiss
                        </button>
                      </div>
                    )}
                    {c.status === 'review' && !isOrphan && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => decide(c.id, 'confirm')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md inline-flex items-center gap-1"
                        >
                          <CheckCircle size={12}/> Confirm
                        </button>
                        <button
                          onClick={() => decide(c.id, 'reject')}
                          className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md inline-flex items-center gap-1"
                        >
                          <X size={12}/> Reject
                        </button>
                      </div>
                    )}
                    {c.status === 'confirmed' && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1">
                        <CheckCircle size={10}/> Confirmed
                      </span>
                    )}
                    {c.status === 'auto_matched' && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border bg-sky-50 text-sky-700 border-sky-200 inline-flex items-center gap-1">
                        <Sparkles size={10}/> Auto
                      </span>
                    )}
                    {c.status === 'rejected' && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border bg-slate-50 text-slate-500 border-slate-200 inline-flex items-center gap-1">
                        <AlertCircle size={10}/> Rejected
                      </span>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
